import { useState } from 'react';
import type { Action, Billet, FlagName, InfoPlacement } from '../../api/types';
import { STATUT_BILLET, STATUT_BILLET_VARIANT } from '../../lib/labels';
import { formatDate } from '../../lib/format';
import Modal from '../Modal';
import { Badge, Button, Field, Input, Select } from '../ui';
import BilletActionsMenu, { type MenuGroup } from './BilletActionsMenu';

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  let binary = '';
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.12em] text-outline">{label}</dt>
      <dd className="break-words text-sm text-white">{value}</dd>
    </div>
  );
}

/** Action de statut simple : confirmée dans une modale qui dit ce que l'app verra. */
interface StatutAction {
  key: string;
  label: string;
  consequence: string;
  danger?: boolean;
  action: Action;
}

type Ouverte = { kind: 'infos' } | { kind: 'statut'; action: StatutAction } | { kind: 'placement' } | { kind: 'maquette' } | null;

/** Billet : carte sombre, titre lisible, infos détaillées et actions en modales. */
export default function BilletCard({
  billet,
  onAction,
  busy,
}: {
  billet: Billet;
  onAction: (action: Action) => void;
  busy: boolean;
}) {
  const [ouverte, setOuverte] = useState<Ouverte>(null);
  const [placement, setPlacement] = useState<InfoPlacement[]>([]);
  const [maquetteFlag, setMaquetteFlag] = useState<FlagName>('actionCodeBarre');
  const [maquette, setMaquette] = useState<string | null>(null);
  const fermer = () => setOuverte(null);
  const id = billet.idBillet;

  const statuts: StatutAction[] = [
    {
      key: 'setResale',
      label: 'Mettre en revente',
      consequence: 'statut 3 + date. Carte « En revente » avec date.',
      action: { action: 'setResale', body: { idBillet: id } },
    },
    {
      key: 'setSold',
      label: 'Marquer revendu',
      consequence: 'statut 4 + date. Carte « Revendu » avec date.',
      action: { action: 'setSold', body: { idBillet: id } },
    },
    {
      key: 'resetTicket',
      label: 'Remettre disponible',
      consequence: 'statut 0, dates et bénéficiaire effacés. Annule un transfert en cours.',
      action: { action: 'resetTicket', body: { idBillet: id } },
    },
    {
      key: 'simulateTransferCancelled',
      label: 'Simuler « transfert annulé »',
      consequence: 'côté récepteur : statut 5, QR retiré, dialogue bloquant, sans rechargement.',
      action: { action: 'simulateTransferCancelled', body: { idBillet: id } },
    },
    {
      key: 'simulateTransferExpired',
      label: 'Simuler « transfert expiré »',
      consequence: 'statut 7 signalé une fois. Dialogue « transfert expiré » puis retour à 0 en local.',
      action: { action: 'simulateTransferExpired', body: { idBillet: id } },
    },
    {
      key: 'cancelTicket',
      label: 'Annuler le billet',
      consequence: 'statut 6 + date. Carte « Annulé ».',
      danger: true,
      action: { action: 'cancelTicket', body: { idBillet: id } },
    },
  ];

  const groups: MenuGroup[] = [
    {
      title: 'Statut',
      items: statuts.map((s) => ({
        key: s.key,
        label: s.label,
        ...(s.danger ? { danger: true } : {}),
        onSelect: () => setOuverte({ kind: 'statut', action: s }),
      })),
    },
    {
      title: 'Données',
      items: [
        {
          key: 'placement',
          label: 'Modifier le placement',
          onSelect: () => {
            setPlacement(billet.infoPlacement.map((p) => ({ ...p })));
            setOuverte({ kind: 'placement' });
          },
        },
        {
          key: 'maquette',
          label: 'Changer la maquette',
          onSelect: () => {
            setMaquette(null);
            setOuverte({ kind: 'maquette' });
          },
        },
      ],
    },
  ];

  const beneficiaire = [billet.nomBeneficiaire, billet.prenomBeneficiaire].filter(Boolean).join(' ') || '—';

  return (
    <article className="rounded-lg border border-card-border bg-surface-neutral p-4 shadow-elevation-3">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <div>
            <h3 className="text-xl font-bold text-white">Billet {id}</h3>
            <p className="font-mono text-[11px] text-outline">{billet.guidbil}</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge variant={STATUT_BILLET_VARIANT[billet.statutBillet]}>
              {billet.statutBillet} — {STATUT_BILLET[billet.statutBillet]}
            </Badge>
            <Badge variant={billet.numeroCodeBarre ? 'success' : 'dark'}>
              {billet.numeroCodeBarre ? `code barre ${billet.numeroCodeBarre}` : 'sans code barre'}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setOuverte({ kind: 'infos' })}>
            Afficher les infos
          </Button>
          <BilletActionsMenu groups={groups} disabled={busy} />
        </div>
      </header>

      {ouverte?.kind === 'infos' ? (
        <Modal open title={`Billet ${id}`} onClose={fermer}>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Info label="Prix" value={`${billet.prixBillet} €`} />
            <Info label="Bénéficiaire" value={beneficiaire} />
            <Info label="Import" value={formatDate(billet.dateImport)} />
            <Info label="Transfert" value={formatDate(billet.dateTransfert)} />
            <Info label="Mise en revente" value={formatDate(billet.dateMiseRevente)} />
            <Info label="Revente" value={formatDate(billet.dateRevente)} />
            <Info label="Annulation" value={formatDate(billet.dateAnnulation)} />
            <Info label="Code barre" value={billet.numeroCodeBarre || '—'} />
            <div className="col-span-2">
              <Info
                label="Placement"
                value={billet.infoPlacement.map((p) => `${p.LibellePlace} : ${p.ValeurPlace}`).join(' · ') || '—'}
              />
            </div>
            <div className="col-span-2">
              <Info label="guidbil" value={billet.guidbil} />
            </div>
          </dl>
        </Modal>
      ) : null}

      {ouverte?.kind === 'statut' ? (
        <Modal
          open
          title={ouverte.action.label}
          onClose={fermer}
          footer={
            <>
              <Button size="sm" variant="ghost" onClick={fermer}>
                Annuler
              </Button>
              <Button
                size="sm"
                variant={ouverte.action.danger ? 'danger' : 'primary'}
                disabled={busy}
                onClick={() => {
                  onAction(ouverte.action.action);
                  fermer();
                }}
              >
                Appliquer
              </Button>
            </>
          }
        >
          <p className="text-sm text-on-surface-variant">
            <span className="font-semibold text-white">Dans l’app :</span> {ouverte.action.consequence}
          </p>
        </Modal>
      ) : null}

      {ouverte?.kind === 'placement' ? (
        <Modal
          open
          title="Modifier le placement"
          onClose={fermer}
          footer={
            <>
              <Button size="sm" variant="ghost" onClick={fermer}>
                Annuler
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={busy}
                onClick={() => {
                  onAction({ action: 'setPlacement', body: { idBillet: id, infoPlacement: placement } });
                  fermer();
                }}
              >
                Enregistrer
              </Button>
            </>
          }
        >
          <div className="space-y-2">
            {placement.map((line, index) => (
              <div key={index} className="grid grid-cols-2 gap-2">
                <Input
                  aria-label={`Libellé ${index + 1}`}
                  placeholder="Libellé"
                  value={line.LibellePlace}
                  onChange={(e) =>
                    setPlacement((current) =>
                      current.map((p, i) => (i === index ? { ...p, LibellePlace: e.target.value } : p)),
                    )
                  }
                />
                <Input
                  aria-label={`Valeur ${index + 1}`}
                  placeholder="Valeur"
                  value={line.ValeurPlace}
                  onChange={(e) =>
                    setPlacement((current) =>
                      current.map((p, i) => (i === index ? { ...p, ValeurPlace: e.target.value } : p)),
                    )
                  }
                />
              </div>
            ))}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPlacement((current) => [...current, { LibellePlace: '', ValeurPlace: '' }])}
            >
              + Ajouter une ligne
            </Button>
          </div>
        </Modal>
      ) : null}

      {ouverte?.kind === 'maquette' ? (
        <Modal
          open
          title="Changer la maquette"
          onClose={fermer}
          footer={
            <>
              <Button size="sm" variant="ghost" onClick={fermer}>
                Annuler
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={busy || !maquette}
                onClick={() => {
                  if (!maquette) return;
                  onAction({ action: 'setMaquette', body: { idBillet: id, maquetteBillet: maquette, flag: maquetteFlag } });
                  fermer();
                }}
              >
                Enregistrer
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field label="Image du billet (PNG ou JPEG)">
              <input
                type="file"
                accept="image/png,image/jpeg"
                aria-label="Image du billet"
                className="text-xs text-on-surface-variant file:mr-2 file:rounded file:border-0 file:bg-primary file:px-2 file:py-1 file:text-xs file:font-semibold file:text-white"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  setMaquette(file ? await fileToBase64(file) : null);
                }}
              />
            </Field>
            <Field label="Rechargement de l’app" hint="Drapeau à lever : question ouverte Ticketmaster (guide §11 A)">
              <Select value={maquetteFlag} onChange={(e) => setMaquetteFlag(e.target.value as FlagName)}>
                <option value="actionCodeBarre">Recharger la commande et ses QR codes</option>
                <option value="action">Recharger la commande</option>
                <option value="none">Ne pas recharger</option>
              </Select>
            </Field>
          </div>
        </Modal>
      ) : null}
    </article>
  );
}
